import React, { useState, useEffect } from 'react';
import { Card, List, Badge, Button, Space, message, Tabs, Modal, Descriptions } from 'antd';
import { BellOutlined, CheckOutlined, CloseOutlined, ReloadOutlined, UserOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import AdminApplicationService from '../../../../services/AdminApplicationService';
import { APPLICATION_STATUS, APPLICATION_STATUS_DISPLAY } from '../../../../constants/applicationStatus';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

const Application = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState({});
    const [downloadLoading, setDownloadLoading] = useState({});
    const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        fetchApplications();
    }, []);

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const response = await AdminApplicationService.getAllApplications(0, 100);
            setApplications(response.data.list || []);
        } catch (error) {
            message.error(<IntlMessage id={'dashboard.admin.applications.fetchError'} fallback={'Не удалось загрузить заявки'} />);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (applicationId) => {
        setStatusLoading({...statusLoading, [applicationId]: true});
        try {
            await AdminApplicationService.updateApplicationStatus(applicationId, APPLICATION_STATUS.APPROVED);
            setApplications(applications.map(app =>
                app.id === applicationId ? { ...app, status: APPLICATION_STATUS.APPROVED } : app
            ));
            message.success(<IntlMessage id={'dashboard.admin.applications.approveSuccess'} fallback={'Заявка успешно одобрена'} />);
        } catch (error) {
            message.error(<IntlMessage id={'dashboard.admin.applications.approveError'} fallback={'Не удалось одобрить заявку'} />);
        } finally {
            setStatusLoading({...statusLoading, [applicationId]: false});
        }
    };

    const handleReject = async (applicationId) => {
        setStatusLoading({...statusLoading, [applicationId]: true});
        try {
            await AdminApplicationService.updateApplicationStatus(applicationId, APPLICATION_STATUS.REJECTED);
            setApplications(applications.map(app =>
                app.id === applicationId ? { ...app, status: APPLICATION_STATUS.REJECTED } : app
            ));
            message.success(<IntlMessage id={'dashboard.admin.applications.rejectSuccess'} fallback={'Заявка успешно отклонена'} />);
        } catch (error) {
            message.error(<IntlMessage id={'dashboard.admin.applications.rejectError'} fallback={'Не удалось отклонить заявку'} />);
        } finally {
            setStatusLoading({...statusLoading, [applicationId]: false});
        }
    };

    const handleCreateContract = async (applicationId) => {
        setStatusLoading({...statusLoading, [applicationId]: true});
        try {
            await AdminApplicationService.updateApplicationStatus(applicationId, APPLICATION_STATUS.CONTRACT_CONCLUSION);
            setApplications(applications.map(app =>
                app.id === applicationId ? { ...app, status: APPLICATION_STATUS.CONTRACT_CONCLUSION } : app
            ));
            message.success(<IntlMessage id={'dashboard.admin.applications.contractSuccess'} fallback={'Договор успешно создан'} />);
        } catch (error) {
            message.error(<IntlMessage id={'dashboard.admin.applications.contractError'} fallback={'Не удалось создать договор'} />);
        } finally {
            setStatusLoading({...statusLoading, [applicationId]: false});
        }
    };

    const handleDownloadContract = async (applicationId) => {
        setDownloadLoading({...downloadLoading, [applicationId]: true});
        try {
            const response = await AdminApplicationService.getContractDraft(applicationId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'dogovor-draft.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            message.error('Не удалось скачать договор');
        } finally {
            setDownloadLoading({...downloadLoading, [applicationId]: false});
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case APPLICATION_STATUS.SUBMITTED: return 'processing';
            case APPLICATION_STATUS.APPROVED: return 'success';
            case APPLICATION_STATUS.REJECTED: return 'error';
            case APPLICATION_STATUS.CONTRACT_CONCLUSION: return 'warning';
            case APPLICATION_STATUS.COMPLETED: return 'success';
            default: return 'default';
        }
    };

    const filterApplicationsByStatus = (status) => {
        return applications.filter(app => app.status === status);
    };

    const handleViewUserDetails = (user) => {
        setSelectedUser(user);
        setUserDetailModalVisible(true);
    };

    const renderApplicationItem = (item) => {
        const actions = [];

        if (item.status === APPLICATION_STATUS.SUBMITTED) {
            actions.push(
                <Button
                    key="approve"
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={statusLoading[item.id]}
                    onClick={() => handleApprove(item.id)}
                >
                    <IntlMessage id={'approve'} fallback={'Одобрить'} />
                </Button>,
                <Button
                    key="reject"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    loading={statusLoading[item.id]}
                    onClick={() => handleReject(item.id)}
                >
                    <IntlMessage id={'reject'} fallback={'Отклонить'} />
                </Button>
            );
        }

        if (item.status === APPLICATION_STATUS.APPROVED) {
            actions.push(
                <Button
                    key="createContract"
                    type="primary"
                    size="small"
                    icon={<FileTextOutlined />}
                    loading={statusLoading[item.id]}
                    onClick={() => handleCreateContract(item.id)}
                >
                    <IntlMessage id={'createContract'} fallback={'Создать договор'} />
                </Button>,
                <Button
                    key="reject"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    loading={statusLoading[item.id]}
                    onClick={() => handleReject(item.id)}
                >
                    <IntlMessage id={'reject'} fallback={'Отклонить'} />
                </Button>
            );
        }

        if (item.status === APPLICATION_STATUS.CONTRACT_CONCLUSION) {
            actions.push(
                <Button
                    key="download"
                    type="default"
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadLoading[item.id]}
                    onClick={() => handleDownloadContract(item.id)}
                >
                    Скачать договор
                </Button>,
                <Button
                    key="reject"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    loading={statusLoading[item.id]}
                    onClick={() => handleReject(item.id)}
                >
                    <IntlMessage id={'reject'} fallback={'Отклонить'} />
                </Button>
            );
        }

        if (item.status === APPLICATION_STATUS.COMPLETED) {
            actions.push(
                <Button
                    key="download"
                    type="default"
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadLoading[item.id]}
                    onClick={() => handleDownloadContract(item.id)}
                >
                    Скачать договор
                </Button>
            );
        }

        return (
            <List.Item actions={actions}>
                <List.Item.Meta
                    title={
                        <Space>
                            {item.crops}
                            <Badge
                                status={getStatusColor(item.status)}
                                text={APPLICATION_STATUS_DISPLAY[item.status] || item.status}
                            />
                        </Space>
                    }
                    description={
                        <>
                            <div>
                                <strong>Культуры:</strong> {item.crops}<br/>
                                <strong>Планируемый объем воды:</strong> {item.plannedWaterVolume} м³<br/>
                                <strong>Период:</strong> {dayjs(item.startDate).format('DD.MM.YYYY')} - {dayjs(item.endDate).format('DD.MM.YYYY')}
                                {item.channelName && <><br/><strong>Канал:</strong> {item.channelName}</>}
                                {item.channelNumber && <> (№{item.channelNumber})</>}
                                {item.irrigatedArea && <><br/><strong>Орошаемая площадь:</strong> {item.irrigatedArea} га</>}
                                {item.specificWaterConsumptionRate && <><br/><strong>Удельная норма:</strong> {item.specificWaterConsumptionRate} м³</>}
                                {item.location && <><br/><strong>Местоположение:</strong> {item.location}</>}
                                {item.cadastralNumber && <><br/><strong>Кадастровый номер:</strong> {item.cadastralNumber}</>}
                                {item.waterMeteringMethod && <><br/><strong>Учет воды:</strong> {item.waterMeteringMethod}</>}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                <span>
                                    Пользователь: {item.user?.profile?.fullName || item.user?.username || 'N/A'} | Создано: {dayjs(item.createdAt).format('DD.MM.YYYY HH:mm')}
                                    {item.user && (
                                        <>
                                            {' | '}
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<UserOutlined />}
                                                onClick={() => handleViewUserDetails(item.user)}
                                                style={{
                                                    padding: '0 4px',
                                                    height: 'auto',
                                                    fontSize: '12px',
                                                    lineHeight: '1',
                                                    color: '#1890ff'
                                                }}
                                            >
                                                Подробнее
                                            </Button>
                                        </>
                                    )}
                                </span>
                            </div>
                        </>
                    }
                />
            </List.Item>
        );
    };

    return (
        <>
            <Card
                title={
                    <>
                        <BellOutlined style={{ marginRight: 8 }} />
                        <IntlMessage id={'dashboard.admin.applications.title'} fallback={'Заявки на водопользование'} />
                    </>
                }
                extra={
                    <Button
                        type="default"
                        icon={<ReloadOutlined />}
                        onClick={fetchApplications}
                        loading={loading}
                    >
                        <IntlMessage id={'refresh'} fallback={'Обновить'} />
                    </Button>
                }
                style={{ height: '100%' }}
            >
                <Tabs defaultActiveKey={APPLICATION_STATUS.SUBMITTED}>
                    <TabPane
                        tab={
                            <Badge count={filterApplicationsByStatus(APPLICATION_STATUS.SUBMITTED).length} size="small">
                                <IntlMessage id={'submitted'} fallback={'Поданные'} />
                            </Badge>
                        }
                        key={APPLICATION_STATUS.SUBMITTED}
                    >
                        <List
                            loading={loading}
                            dataSource={filterApplicationsByStatus(APPLICATION_STATUS.SUBMITTED)}
                            renderItem={renderApplicationItem}
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <Badge count={filterApplicationsByStatus(APPLICATION_STATUS.APPROVED).length} size="small">
                                <IntlMessage id={'approved'} fallback={'Одобренные'} />
                            </Badge>
                        }
                        key={APPLICATION_STATUS.APPROVED}
                    >
                        <List
                            loading={loading}
                            dataSource={filterApplicationsByStatus(APPLICATION_STATUS.APPROVED)}
                            renderItem={renderApplicationItem}
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <Badge count={filterApplicationsByStatus(APPLICATION_STATUS.CONTRACT_CONCLUSION).length} size="small">
                                <IntlMessage id={'contract'} fallback={'Договор'} />
                            </Badge>
                        }
                        key={APPLICATION_STATUS.CONTRACT_CONCLUSION}
                    >
                        <List
                            loading={loading}
                            dataSource={filterApplicationsByStatus(APPLICATION_STATUS.CONTRACT_CONCLUSION)}
                            renderItem={renderApplicationItem}
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <Badge count={filterApplicationsByStatus(APPLICATION_STATUS.COMPLETED).length} size="small">
                                <IntlMessage id={'completed'} fallback={'Завершенные'} />
                            </Badge>
                        }
                        key={APPLICATION_STATUS.COMPLETED}
                    >
                        <List
                            loading={loading}
                            dataSource={filterApplicationsByStatus(APPLICATION_STATUS.COMPLETED)}
                            renderItem={renderApplicationItem}
                        />
                    </TabPane>

                    <TabPane
                        tab={
                            <Badge count={filterApplicationsByStatus(APPLICATION_STATUS.REJECTED).length} size="small">
                                <IntlMessage id={'rejected'} fallback={'Отклоненные'} />
                            </Badge>
                        }
                        key={APPLICATION_STATUS.REJECTED}
                    >
                        <List
                            loading={loading}
                            dataSource={filterApplicationsByStatus(APPLICATION_STATUS.REJECTED)}
                            renderItem={renderApplicationItem}
                        />
                    </TabPane>
                </Tabs>
            </Card>

            <Modal
                title={<IntlMessage id={'dashboard.admin.applications.userDetails'} fallback={'Детали пользователя'} />}
                open={userDetailModalVisible}
                onCancel={() => setUserDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setUserDetailModalVisible(false)}>
                        <IntlMessage id={'close'} fallback={'Закрыть'} />
                    </Button>
                ]}
                width={600}
            >
                {selectedUser && (
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label={<IntlMessage id={'username'} fallback={'Имя пользователя'} />}>
                            {selectedUser.username}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'fullName'} fallback={'ФИО'} />}>
                            {selectedUser.profile?.fullName || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'iin'} fallback={'ИИН'} />}>
                            {selectedUser.profile?.iin || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'phone'} fallback={'Контактный телефон'} />}>
                            {selectedUser.profile?.telNumber || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'email'} fallback={'Email'} />}>
                            {selectedUser.profile?.email || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'registrationAddress'} fallback={'Адрес регистрации'} />}>
                            {selectedUser.profile?.registrationAddress || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'residenceAddress'} fallback={'Адрес фактического проживания'} />}>
                            {selectedUser.profile?.residenceAddress || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'businessType'} fallback={'Тип хозяйства'} />}>
                            {selectedUser.profile?.businessType || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'organizationName'} fallback={'Название организации'} />}>
                            {selectedUser.profile?.organizationName || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bin'} fallback={'БИН'} />}>
                            {selectedUser.profile?.bin || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'position'} fallback={'Должность'} />}>
                            {selectedUser.profile?.position || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bankAccount'} fallback={'ИИК (банковский счет)'} />}>
                            {selectedUser.profile?.bankAccount || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bankBik'} fallback={'БИК банка'} />}>
                            {selectedUser.profile?.bankBik || 'Не указано'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bankName'} fallback={'Название банка'} />}>
                            {selectedUser.profile?.bankName || 'Не указано'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </>
    );
};

export default Application;
